import { FC, useEffect, useState } from "react";
import { Modal } from "../../../components";
import { useDeleteMedia, useMedia, useUpdateLoanVendor } from "../../../hooks";
import { GetMediaResponse, Vendor } from "../../../hooks/api/types";
import {
  ArrowLeftIcon,
  ButtonLoaderIcon,
  EyeIcon,
  TrashIcon,
} from "../../../assets/icons";
import { truncate } from "../../../utils";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  documentIds: string[];
  vendor?: Vendor;
  loanApplicationId?: string;
  onDocumentDelete?: () => void;
};

const TradeLicenseModal: FC<Props> = ({
  isOpen,
  onClose,
  documentIds,
  vendor,
  loanApplicationId,
  onDocumentDelete,
}) => {
  const [documents, setDocuments] = useState<GetMediaResponse[]>([]);
  const [selectedDocument, setSelectedDocument] =
    useState<GetMediaResponse | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);
  const { get } = useMedia();
  const { mutateAsync: deleteMediaById } = useDeleteMedia();
  const { mutateAsync: updateLoanVendor } = useUpdateLoanVendor();

  useEffect(() => {
    if (isOpen && documentIds.length > 0) {
      Promise.all(documentIds.map((id) => get({ id }))).then((docs) => {
        setDocuments(docs);
      });
    }
  }, [isOpen, documentIds]);

  const handleRemoveDocument = async (documentId: string) => {
    if (!documents?.length) {
      return;
    }

    const removedDoc = documents.find((doc) => doc.media?.id === documentId);
    if (!removedDoc) {
      return;
    }

    try {
      setDeleting(documentId);

      if (removedDoc.media?.id) {
        await deleteMediaById(removedDoc.media.id);
      }

      if (vendor && loanApplicationId && vendor.loanVendorId) {
        const updatedDocumentIds =
          vendor.documents
            ?.filter((doc) => doc.documentId !== documentId)
            .map((doc) => doc.documentId) || [];

        await updateLoanVendor({
          loanApplicationId,
          loanVendorId: vendor.loanVendorId,
          name: vendor.name,
          country: vendor.country,
          type: vendor.type,
          tradeLicenseDocumentIds: updatedDocumentIds,
        });
      }

      const updatedList = documents.filter(
        (doc) => doc.media?.id !== documentId
      );
      setDocuments(updatedList);

      if (onDocumentDelete) {
        onDocumentDelete();
      }

      if (updatedList.length === 0) {
        onClose();
      }
    } catch (error) {
      console.error("Error deleting document:", error);
    } finally {
      setDeleting(null);
    }
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title="Trade licenses"
        titleAlign="left"
        headerLeft={
          <button
            type="button"
            onClick={onClose}
            className="cx-flex cx-items-center cx-gap-2 cx-text-text-brand cx-font-medium cx-border cx-border-stroke-primary cx-rounded-full cx-px-2 cx-py-1 cx-text-sm hover:cx-opacity-80"
          >
            <ArrowLeftIcon className="cx-w-4 cx-h-4" />
            Back
          </button>
        }
        className="cx-min-w-[826px]"
      >
        <div className="cx-flex cx-flex-col cx-gap-4 cx-rounded-lg cx-border cx-border-stroke-primary">
          <div className=" cx-rounded-lg cx-p-4">
            <div className="cx-grid cx-grid-cols-3 cx-gap-4 cx-text-sm cx-font-normal cx-text-text-secondary cx-mb-4">
              <div>Name</div>
              <div>Format</div>
              <div>Actions</div>
            </div>

            {documents.map((doc) => (
              <div
                key={doc.media?.id}
                className="cx-grid cx-grid-cols-3 cx-gap-4 cx-items-center cx-py-3 cx-border-t cx-border-stroke-primary"
              >
                <div className="cx-flex cx-items-center cx-gap-3">
                  <div className="cx-w-10 cx-h-12 cx-rounded cx-bg-background-default cx-flex cx-items-center cx-justify-center">
                    {doc.thumbnailUrl ? (
                      <img
                        src={doc.thumbnailUrl}
                        alt="Document preview"
                        className="cx-w-full cx-h-full cx-object-cover cx-rounded"
                      />
                    ) : (
                      <div className="cx-text-text-tertiary cx-text-xs">
                        PDF
                      </div>
                    )}
                  </div>
                  <div className="cx-text-sm cx-font-normal">Trade License</div>
                </div>
                <div className="cx-text-sm cx-text-text-secondary">PDF</div>
                <div className="cx-flex cx-gap-2">
                  <button
                    className="cx-group cx-bg-background-secondary cx-w-8 cx-h-8 cx-flex cx-justify-center cx-items-center cx-rounded-full cx-border cx-border-stroke-primary cx-cursor-pointer hover:cx-bg-background-brand"
                    onClick={() => setSelectedDocument(doc)}
                  >
                    <EyeIcon className="cx-text-text-brand group-hover:cx-text-text-inverse cx-w-4 cx-h-4" />
                  </button>
                  <button
                    className="cx-group cx-w-8 cx-bg-background-secondary cx-h-8 cx-flex cx-justify-center cx-items-center cx-rounded-full cx-cursor-pointer hover:cx-bg-background-error"
                    onClick={() => {
                      if (doc.media?.id) {
                        handleRemoveDocument(doc.media.id);
                      }
                    }}
                    disabled={deleting === doc.media?.id}
                  >
                    {deleting === doc.media?.id ? (
                      <ButtonLoaderIcon className="cx-animate-spin cx-text-text-error group-hover:cx-text-text-inverse cx-w-4 cx-h-4" />
                    ) : (
                      <TrashIcon className="cx-text-text-error group-hover:cx-text-text-inverse cx-w-4 cx-h-4" />
                    )}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </Modal>

      {selectedDocument && (
        <Modal
          isOpen={!!selectedDocument}
          onClose={() => setSelectedDocument(null)}
          title={truncate(
            selectedDocument?.media?.fileName ?? "Trade License",
            45
          )}
          titleAlign="left"
          headerLeft={
            <button
              type="button"
              onClick={() => setSelectedDocument(null)}
              className="cx-flex cx-items-center cx-gap-2 cx-text-text-brand cx-font-medium cx-text-sm hover:cx-opacity-80"
            >
              <ArrowLeftIcon className="cx-w-5 cx-h-5" />
              Back
            </button>
          }
          className="cx-max-w-[960px]"
        >
          <iframe
            src={selectedDocument?.url}
            style={{ width: 576, height: 768 }}
            frameBorder="0"
          ></iframe>
        </Modal>
      )}
    </>
  );
};

export default TradeLicenseModal;
